import useEmblaCarousel from 'embla-carousel-react'
import { ChevronLeft, ChevronRight, FileText, ShoppingCart } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'

import { cn } from '../../../lib/cn.js'
import {
  withOchatbotEcommerceAddToCartTracking,
  withOchatbotEcommerceViewTracking,
} from '../../../lib/chatUtils.js'
import {
  clampStandardCardWidthToViewport,
  normalizeSuggestionStripSize,
  resolveLandscapeImageBandPx,
  resolveSuggestionStripSizeTokens,
  SUGGESTION_STRIP_CARD_WIDTH_PX,
} from '../../../lib/suggestionCardLayout.js'

function stripHtml(html) {
  if (html == null || typeof html !== 'string') return ''
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

function ListCardImage({ imageUrl, alt, defaultBandPx, minLandscapeBandPx, fallbackIcon }) {
  const [bandPx, setBandPx] = useState(defaultBandPx)

  useEffect(() => {
    setBandPx(defaultBandPx)
  }, [defaultBandPx, imageUrl])

  if (!imageUrl) {
    return (
      <div
        style={{ height: defaultBandPx }}
        className="flex w-full items-center justify-center text-[var(--color-muted-foreground)]"
      >
        {fallbackIcon}
      </div>
    )
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      style={{ height: bandPx, maxHeight: bandPx }}
      className="mx-auto w-full object-contain"
      loading="lazy"
      onLoad={(event) => {
        const img = event.currentTarget
        setBandPx(
          resolveLandscapeImageBandPx({
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight,
            defaultBandPx,
            cardWidthPx: SUGGESTION_STRIP_CARD_WIDTH_PX,
            minBandPx: minLandscapeBandPx,
          }),
        )
      }}
    />
  )
}

/**
 * List/carousel message — rich product, page, or article cards.
 * Embla replaces `react-slick` and works inside Shadow DOM.
 * Card sizing aligns with `SuggestionsStrip` (240px wide, size-token heights).
 */
export function ListMessage({ message, conf, ochatbot }) {
  const elements = Array.isArray(message.elements) ? message.elements : []
  const stripSize = normalizeSuggestionStripSize(conf?.suggestionStripSize)
  const sizeTokens = resolveSuggestionStripSizeTokens(stripSize)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: false,
    loop: false,
  })

  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    const multi = elements.length > 1
    setCanScrollPrev(multi)
    setCanScrollNext(multi)
  }, [emblaApi, elements.length])

  const scrollPrevCircular = useCallback(() => {
    if (!emblaApi) return
    const snaps = emblaApi.scrollSnapList()
    if (snaps.length <= 1) return
    const i = emblaApi.selectedScrollSnap()
    if (i <= 0) emblaApi.scrollTo(snaps.length - 1)
    else emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNextCircular = useCallback(() => {
    if (!emblaApi) return
    const snaps = emblaApi.scrollSnapList()
    if (snaps.length <= 1) return
    const i = emblaApi.selectedScrollSnap()
    const last = snaps.length - 1
    if (i >= last) emblaApi.scrollTo(0)
    else emblaApi.scrollNext()
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
    queueMicrotask(onSelect)
    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  const syncSlideBasisToViewport = useCallback(() => {
    if (!emblaApi) return
    const node = emblaApi.rootNode()
    if (!(node instanceof HTMLElement)) return
    const w = node.getBoundingClientRect().width
    if (!(w > 0)) return
    const px = clampStandardCardWidthToViewport(w)
    node.style.setProperty('--list-carousel-slide-basis', `${px}px`)
  }, [emblaApi])

  useLayoutEffect(() => {
    if (!emblaApi || elements.length === 0) return
    syncSlideBasisToViewport()
    emblaApi.reInit()
    emblaApi.scrollTo(0)
  }, [emblaApi, message.id, elements.length, syncSlideBasisToViewport])

  useEffect(() => {
    if (!emblaApi || typeof ResizeObserver === 'undefined') return
    const node = emblaApi.rootNode()
    if (!node) return
    const ro = new ResizeObserver(() => {
      syncSlideBasisToViewport()
      emblaApi.reInit()
    })
    ro.observe(node)
    return () => ro.disconnect()
  }, [emblaApi, syncSlideBasisToViewport])

  if (elements.length === 0) return null

  const handleCardClick = (element) => {
    const url = element.url || element.item_url || element.buttons?.[0]?.url
    if (url) {
      const isProduct =
        element.type === 'product' || Boolean(element.add_to_cart_url)
      const trackedUrl = isProduct
        ? withOchatbotEcommerceViewTracking(url)
        : url
      ochatbot?.callAPI(
        `ometrics-button-click~${trackedUrl}`,
        element.title,
        true,
      )
      window.open(trackedUrl, '_blank', 'noopener')
    }
  }

  const handleAddToCart = (element, e) => {
    e.stopPropagation()
    if (element.add_to_cart_url) {
      const cartUrl = withOchatbotEcommerceAddToCartTracking(
        element.add_to_cart_url,
      )
      ochatbot?.callAPI(
        `ometrics-button-click~${cartUrl}`,
        element.title,
        true,
      )
      window.open(cartUrl, '_blank', 'noopener')
    }
  }

  const buttonSurfaceStyle = {
    backgroundColor: conf?.widgetButtonColor || '#1976d2',
    color: conf?.widgetButtonTextColor || '#ffffff',
  }

  return (
    <div className="ometrics-msg-list relative mt-2 w-full min-w-0">
      <div className="w-full min-w-0 overflow-hidden" ref={emblaRef}>
        <div className="flex w-full min-w-0 gap-3">
          {elements.map((el, index) => {
            const isProduct = el.type === 'product' || Boolean(el.add_to_cart_url)
            const buttons = el.buttons || []
            const viewUrl = el.url || el.item_url || buttons[0]?.url
            const fromBtnTitle =
              typeof buttons[0]?.title === 'string'
                ? stripHtml(buttons[0].title).trim()
                : ''
            const viewButtonLabel =
              fromBtnTitle || (isProduct ? 'View Details' : 'Read More')
            const imageUrl =
              el.image_url || el.image || conf?.defaultCardImage || ''
            const subtitle = el.subtitle || el.excerpt
            const showFooter = Boolean(viewUrl || el.add_to_cart_url)

            return (
              <div
                key={`${message.id}-el-${index}`}
                style={{
                  height: sizeTokens.cardHeightPx,
                  maxHeight: sizeTokens.cardHeightPx,
                }}
                className={cn(
                  'shrink-0 grow-0 overflow-hidden rounded-xl bg-[var(--color-surface)] text-left',
                  'w-[var(--list-carousel-slide-basis,240px)] max-w-[var(--list-carousel-slide-basis,240px)]',
                  'transition-transform duration-200 hover:-translate-y-0.5',
                )}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCardClick(el)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleCardClick(el)
                    }
                  }}
                  className={cn(
                    'flex min-h-0 min-w-0 flex-1 cursor-pointer flex-col overflow-hidden outline-none',
                    'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-brand)]',
                  )}
                >
                  <div
                    className={cn(
                      'shrink-0 border-b border-[#f8f9fa] bg-[#ffffff] dark:border-[var(--color-border)] dark:bg-[var(--color-muted)]',
                    )}
                  >
                    <ListCardImage
                      imageUrl={imageUrl}
                      alt={el.title || ''}
                      defaultBandPx={sizeTokens.imageBandPx}
                      minLandscapeBandPx={sizeTokens.minLandscapeBandPx}
                      fallbackIcon={
                        isProduct ? (
                          <ShoppingCart className="h-6 w-6 opacity-50" />
                        ) : (
                          <FileText className="h-6 w-6 opacity-50" />
                        )
                      }
                    />
                  </div>

                  <div className="flex min-h-0 flex-1 flex-col gap-0.5 px-2 py-1.5">
                    {el.title ? (
                      <h4
                        className={cn(
                          'line-clamp-2 text-left',
                          sizeTokens.titleClass,
                          'text-[#212529] dark:text-[var(--color-surface-foreground)]',
                        )}
                      >
                        {el.title}
                      </h4>
                    ) : null}
                    {isProduct && (el.price || el.sale_price) ? (
                      <div className="flex flex-wrap items-baseline gap-1">
                        {el.sale_price ? (
                          <span className="text-[12px] font-bold text-[#dc3545]">
                            {el.sale_price}
                          </span>
                        ) : null}
                        {el.price ? (
                          <span
                            className={cn(
                              'text-[12px] font-bold',
                              el.sale_price
                                ? 'text-[#6c757d] line-through'
                                : 'text-[#212529] dark:text-[var(--color-surface-foreground)]',
                            )}
                          >
                            {el.price}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    {subtitle ? (
                      <p
                        className={cn(
                          'line-clamp-2 text-left text-[12px] leading-snug',
                          'text-[#6c757d] dark:text-[var(--color-muted-foreground)]',
                        )}
                      >
                        {stripHtml(String(subtitle))}
                      </p>
                    ) : null}
                  </div>
                </div>

                {showFooter ? (
                  <div className="flex shrink-0 flex-wrap justify-center gap-2 border-t border-[var(--color-border)] px-2 py-2">
                    {viewUrl ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleCardClick(el)
                        }}
                        className="inline-flex min-h-[32px] shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-full px-3 py-1.5 text-center text-[11px] font-medium transition-opacity hover:opacity-90"
                        style={buttonSurfaceStyle}
                      >
                        {viewButtonLabel}
                      </button>
                    ) : null}
                    {el.add_to_cart_url ? (
                      <button
                        type="button"
                        onClick={(e) => handleAddToCart(el, e)}
                        className="inline-flex min-h-[32px] shrink-0 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-medium transition-opacity hover:opacity-90"
                        style={buttonSurfaceStyle}
                      >
                        <ShoppingCart className="h-3.5 w-3.5 shrink-0" />
                        Add to Cart
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>

      {elements.length > 1 && (
        <>
          <button
            type="button"
            disabled={!canScrollPrev}
            onClick={scrollPrevCircular}
            aria-label="Previous"
            className={cn(
              'absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-[var(--color-surface)]/90 p-1 shadow',
              'disabled:opacity-30',
            )}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={!canScrollNext}
            onClick={scrollNextCircular}
            aria-label="Next"
            className={cn(
              'absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-[var(--color-surface)]/90 p-1 shadow',
              'disabled:opacity-30',
            )}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  )
}
